/*
 * HAUS Safety Intelligence v2.0A
 * Shared storefront client for Safety Command Center and Enterprise Dashboard.
 *
 * Production contract: /apps/haus/safety/v1
 * Fallback contract: clearly labeled browser-side daily briefing + NWS screening.
 */
(() => {
  'use strict';

  const VERSION = '2.0A';
  const ROOT_SELECTOR = '[data-haus-safety-root]';
  const DEFAULT_ENDPOINT = '/apps/haus/safety/v1';
  const WEATHER_TTL = 15 * 60 * 1000;
  const SERVICE_RETRY_TTL = 5 * 60 * 1000;
  const instances = new WeakMap();

  if (window.HausSafetyV2 && window.HausSafetyV2.version === VERSION) {
    window.HausSafetyV2.initAll();
    return;
  }

  const PRECAUTIONS = {
    heat: [
      'Confirm cool drinking water, recovery breaks, shade, and the site heat plan.',
      'Account for workload, sun, humidity, protective clothing, and acclimatization.',
      'Use buddy monitoring and escalate symptoms immediately under the site plan.',
      'Reassess work and rest controls when heat exposure or workload changes.'
    ],
    lightning: [
      'Follow the site lightning plan and designated shutdown authority.',
      'Move the crew to a substantial building or hard-topped enclosed vehicle.',
      'Keep workers away from exposed structures, equipment, water, and isolated trees.',
      'Resume only when the site plan and responsible person permit it.'
    ],
    wind: [
      'Secure loose material, signage, temporary protection, and access points.',
      'Reassess lifts, elevated work, suspended loads, and equipment limits.',
      'Confirm current wind and gust conditions with the responsible person.',
      'Pause work when the site plan or manufacturer limits are exceeded.'
    ],
    rain: [
      'Inspect walking surfaces, excavations, access routes, and drainage.',
      'Recheck temporary power, cords, tools, GFCI protection, and wet electrical exposure.',
      'Update traffic control, visibility controls, and housekeeping.',
      'Reopen affected work areas only after the responsible inspection is complete.'
    ],
    cold: [
      'Inspect for ice, frozen surfaces, reduced dexterity, and cold stress.',
      'Confirm warm-up breaks, dry layers, and heated recovery space.',
      'Recheck equipment start-up, access, and material-handling conditions.',
      'Review freeze-thaw effects on excavation, travel paths, and temporary work.'
    ],
    default: [
      'Review site-specific exposure before work begins.',
      'Confirm controls with the competent person or responsible supervisor.',
      'Brief the crew when conditions, people, equipment, or scope change.',
      'Stop work when the conditions no longer match the approved plan.'
    ]
  };

  const TOPICS = [
    { id: 'PTP-01', category: 'Planning', title: 'Plan the Task Before the Task Plans You', text: 'Walk the scope, identify what changed, and confirm the people, equipment, access, and controls required for todayâ€™s work.', action: 'Pause with the crew before starting and resolve any condition that no longer matches the plan.' },
    { id: 'LNF-01', category: 'Line of Fire', title: 'Stay Out of the Line of Fire', text: 'Stored energy, moving equipment, suspended loads, pinch points, and shifting material can change faster than the crew expects.', action: 'Identify the line of fire and establish positions, signals, and exclusion zones before movement begins.' },
    { id: 'HOU-01', category: 'Housekeeping', title: 'Clean Work Is Controlled Work', text: 'Access routes, cords, scrap, packaging, spills, and loose material can turn routine movement into an incident.', action: 'Complete a five-minute cleanup and reopen every walking and working path before production starts.' },
    { id: 'HND-01', category: 'Hand Safety', title: 'Protect the Hands That Build the Work', text: 'Hands are exposed when material shifts, tools bind, guards are bypassed, or body position moves into a pinch point.', action: 'Choose the right glove, inspect the tool, and reposition the work so hands stay outside the danger zone.' },
    { id: 'PPE-01', category: 'PPE', title: 'PPE Only Works When It Is Ready', text: 'Damaged, missing, poorly fitted, or task-incompatible protection creates confidence without control.', action: 'Inspect required PPE, verify fit and compatibility, and replace anything that cannot perform as intended.' },
    { id: 'TOOL-01', category: 'Tools', title: 'Inspect It Before You Send It', text: 'A damaged guard, cord, blade, wheel, trigger, or attachment can turn a familiar tool into an unfamiliar hazard.', action: 'Remove defective equipment from service and confirm the tool and accessory match the material and task.' },
    { id: 'ELEC-01', category: 'Electrical', title: 'Temporary Power Is Still Real Power', text: 'Wet conditions, damaged cords, open boxes, missing protection, and improvised connections increase electrical exposure.', action: 'Inspect the power path from source to tool and correct damaged or unprotected components before energizing.' },
    { id: 'LAD-01', category: 'Ladders', title: 'Set the Ladder Before You Climb It', text: 'Wrong ladder selection, unstable footing, poor angle, damaged components, and overreaching can defeat a simple access plan.', action: 'Inspect and position the ladder for the task, secure the setup, and maintain the required climbing posture.' },
    { id: 'FALL-01', category: 'Fall Protection', title: 'Control the Edge Before Approaching It', text: 'Open sides, floor openings, changing elevations, incomplete protection, and short-duration work still require deliberate planning.', action: 'Confirm the authorized fall-protection method and inspect the complete system before exposure begins.' },
    { id: 'MHE-01', category: 'Material Handling', title: 'Move the Load Without Becoming the Load', text: 'Awkward size, shifting centers of gravity, poor grip, blind travel, and rushed handling create avoidable strain and struck-by exposure.', action: 'Plan the route, use mechanical help when available, and coordinate the move before lifting or carrying.' },
    { id: 'MOB-01', category: 'Mobile Equipment', title: 'Blind Spots Are Work Zones Too', text: 'Operators and ground crews can lose sight of each other around backing equipment, swing areas, loads, noise, and congestion.', action: 'Establish eye contact or an agreed signal, control the travel path, and keep people outside operating zones.' },
    { id: 'RIG-01', category: 'Rigging', title: 'Know the Load. Know the Gear. Know the Path.', text: 'Unknown weights, damaged rigging, poor attachment points, side loading, and unplanned travel can destabilize a lift.', action: 'Have the qualified team confirm the lift plan, rigging condition, capacity, path, and exclusion zone.' },
    { id: 'EXC-01', category: 'Excavation', title: 'The Ground Can Change Without Warning', text: 'Soil, water, spoil placement, adjacent loads, utilities, access, and weather can change excavation conditions throughout the shift.', action: 'Have the competent person inspect current conditions and verify protection, access, and utility controls before entry.' },
    { id: 'SIL-01', category: 'Dust', title: 'Control the Dust at the Source', text: 'Cutting, grinding, drilling, sweeping, and demolition can create airborne exposure before the cloud is obvious.', action: 'Use the approved control method, inspect water or extraction systems, and keep others outside the exposure area.' },
    { id: 'CHEM-01', category: 'Chemical Safety', title: 'Read the Label Before Opening the Container', text: 'Products can introduce inhalation, skin, eye, fire, and mixing hazards when labels, SDS information, or controls are missed.', action: 'Confirm the product, current SDS, required protection, ventilation, storage, and spill response before use.' },
    { id: 'FIRE-01', category: 'Fire Prevention', title: 'Remove the Fuel Before Making the Spark', text: 'Hot work, charging, temporary heat, fuel storage, and combustible debris can combine into an uncontrolled fire path.', action: 'Review ignition sources, combustibles, permits, extinguishing equipment, and the fire-watch plan before starting.' },
    { id: 'EMR-01', category: 'Emergency Readiness', title: 'Know the Move Before the Emergency', text: 'Emergency response slows when the crew does not know who to call, where to assemble, or how responders will reach the work.', action: 'Confirm contacts, alarms, rally points, access routes, first-aid resources, and stop-work authority with the crew.' },
    { id: 'SCF-01', category: 'Scaffolds', title: 'A Platform Is a System', text: 'Access, footing, bracing, planking, guardrails, loading, and modification all affect whether a scaffold remains ready for use.', action: 'Use the required inspection process and do not alter or use a scaffold that has not been released for the task.' },
    { id: 'CON-01', category: 'Concrete', title: 'Control the Pour Before It Controls the Crew', text: 'Placement pressure, hose movement, backing trucks, access, burns, formwork, and finishing congestion can stack exposures quickly.', action: 'Confirm placement sequence, communications, access, washout, PPE, equipment zones, and formwork readiness before concrete arrives.' },
    { id: 'SAW-01', category: 'Cutting', title: 'Plan the Cut and Everything Around It', text: 'Blade selection, binding, kickback, dust, slurry, hidden utilities, noise, and the offcut all belong in the cutting plan.', action: 'Mark the cut, verify the material, inspect the saw and blade, control exposure, and plan where the cut section will move.' },
    { id: 'TRA-01', category: 'Traffic Control', title: 'Make the Travel Path Obvious', text: 'Workers, deliveries, public traffic, backing vehicles, and changing access can overlap when routing is not clear.', action: 'Inspect signs, barriers, lighting, spotter positions, pedestrian paths, and delivery instructions before traffic increases.' },
    { id: 'EYE-01', category: 'Eye and Face', title: 'Match the Protection to the Energy', text: 'Flying material, splash, dust, sparks, and fragments can bypass protection that is damaged, poorly fitted, or wrong for the task.', action: 'Inspect eye and face protection and verify it matches the impact, splash, dust, or radiant-energy exposure.' },
    { id: 'HEAR-01', category: 'Hearing', title: 'If Communication Changes, Recheck the Noise', text: 'Noise can hide alarms, equipment movement, shouted warnings, and gradual hearing damage.', action: 'Use the site noise plan, wear the required protection correctly, and establish a reliable communication method.' },
    { id: 'ENER-01', category: 'Energy Control', title: 'Zero Motion Is Not Zero Energy', text: 'Electrical, hydraulic, pneumatic, mechanical, thermal, and gravity energy can remain after equipment appears stopped.', action: 'Use the employerâ€™s authorized energy-control procedure and verify isolation before servicing, clearing, or entering.' },
    { id: 'FAT-01', category: 'Readiness', title: 'Fatigue Changes the Risk Before It Changes the Work', text: 'Long shifts, heat, poor sleep, medication, stress, and repetitive work can reduce reaction time and decision quality.', action: 'Speak up about reduced readiness, rotate or pause work as directed, and do not let schedule pressure hide impairment.' },
    { id: 'HEAT-01', category: 'Heat', title: 'Water. Rest. Shade. Before the Heat Wins.', text: 'Heat exposure depends on temperature, humidity, radiant heat, air movement, workload, clothing, and acclimatizationâ€”not air temperature alone.', action: 'Confirm the employerâ€™s heat plan, water access, recovery areas, work-rest controls, acclimatization, and symptom response.' },
    { id: 'HEAT-02', category: 'Heat', title: 'Hydration Starts Before Thirst', text: 'High workload, sun, humidity, and protective clothing can increase heat strain before workers recognize the change.', action: 'Begin the shift hydrated, keep water accessible, follow scheduled recovery, and report symptoms immediately under the site plan.' },
    { id: 'HEAT-03', category: 'Heat', title: 'Acclimatization Is Part of the Plan', text: 'New workers and workers returning after time away may be more vulnerable when hot work ramps up too quickly.', action: 'Identify who is not acclimatized and follow the employerâ€™s gradual exposure, observation, and response plan.' },
    { id: 'LGT-01', category: 'Lightning', title: 'When Thunder Roars, Go Indoors', text: 'Lightning can strike before rain arrives and after the storm appears to have moved away.', action: 'Follow the site lightning plan, move to substantial shelter at the first thunder, and resume only under the site plan.' },
    { id: 'LGT-02', category: 'Lightning', title: 'Shelter Is a Location, Not a Guess', text: 'Open structures, trees, equipment canopies, and exposed work areas are not substitutes for designated lightning shelter.', action: 'Confirm the nearest substantial building or hard-topped enclosed vehicle and make sure the full crew knows the route.' },
    { id: 'WND-01', category: 'Wind', title: 'Wind Turns Material Into Movement', text: 'Panels, signs, tarps, dust, elevated work, suspended loads, and equipment stability can change as gusts increase.', action: 'Secure loose material and have the responsible person reassess lifting, access, elevated work, and manufacturer limits.' },
    { id: 'WND-02', category: 'Wind', title: 'A Gust Is a Change in the Lift Plan', text: 'Wind direction and gusts can change load control, tag-line behavior, crane capacity, communication, and landing conditions.', action: 'Pause and have the qualified team verify current wind conditions remain inside the approved lift plan and equipment limits.' },
    { id: 'RAIN-01', category: 'Rain', title: 'Wet Conditions Change More Than Footing', text: 'Rain can affect access, excavation stability, electrical exposure, visibility, traffic, material handling, and housekeeping.', action: 'Reinspect walking surfaces, drainage, temporary power, excavations, traffic control, and stored materials after conditions change.' },
    { id: 'RAIN-02', category: 'Rain', title: 'Reset the Site After the Rain', text: 'Standing water, mud, hidden damage, washed-out routes, and wet electrical components can remain after rainfall ends.', action: 'Complete a post-weather inspection and reopen areas only after the responsible site team confirms controls.' },
    { id: 'COLD-01', category: 'Cold', title: 'Cold Changes Grip, Footing, and Judgment', text: 'Cold, wind, moisture, ice, and reduced dexterity can affect workers, access, tools, vehicles, and material handling.', action: 'Inspect for ice and cold stress, confirm dry layers and warm-up recovery, and adjust work under the employerâ€™s plan.' },
    { id: 'COLD-02', category: 'Cold', title: 'Frozen Ground Is Still a Changing Condition', text: 'Freeze-thaw cycles can affect access, excavation edges, temporary supports, stored material, and equipment travel paths.', action: 'Reinspect the work area after temperature changes and correct unstable, icy, or damaged conditions before work resumes.' },
    { id: 'SPEAK-01', category: 'Stop Work', title: 'See It. Say It. Stop It.', text: 'A plan only works while the conditions, people, equipment, and controls still match what the crew reviewed.', action: 'Stop the task when something changes, protect the area, and bring the responsible person in before restarting.' }
  ];

  const safeParse = (value, fallback = null) => {
    try { return JSON.parse(value); } catch (error) { return fallback; }
  };

  const readStorage = (key) => {
    try { return safeParse(window.localStorage.getItem(key), null); } catch (error) { return null; }
  };

  const writeStorage = (key, value) => {
    try { window.localStorage.setItem(key, JSON.stringify(value)); return true; } catch (error) { return false; }
  };

  const hashString = (value) => {
    let hash = 2166136261;
    const input = String(value || '');
    for (let index = 0; index < input.length; index += 1) {
      hash ^= input.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  };

  const slug = (value) => String(value || 'project')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'project';

  const getDateParts = (timeZone) => {
    const now = new Date();
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone || 'America/Indiana/Indianapolis',
        year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'long'
      }).formatToParts(now).reduce((map, item) => ({ ...map, [item.type]: item.value }), {});
      return {
        key: `${parts.year}${parts.month}${parts.day}`,
        display: new Intl.DateTimeFormat('en-US', {
          timeZone: timeZone || 'America/Indiana/Indianapolis',
          weekday: 'long', month: 'short', day: 'numeric', year: 'numeric'
        }).format(now)
      };
    } catch (error) {
      return {
        key: now.toISOString().slice(0, 10).replace(/-/g, ''),
        display: now.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
      };
    }
  };

  const riskKey = (value) => {
    const risk = String(value || '').toLowerCase();
    if (/tornado|severe thunderstorm|lightning|thunder/.test(risk)) return 'lightning';
    if (/excessive heat|extreme heat|heat/.test(risk)) return 'heat';
    if (/wind|gust/.test(risk)) return 'wind';
    if (/winter|ice|freez|snow|cold|blizzard/.test(risk)) return 'cold';
    if (/flood|rain|shower|drizzle|wet/.test(risk)) return 'rain';
    return 'default';
  };

  const scopeKey = (config) => [config.customerId || 'preview', config.companyId || 'company', config.locationId || 'location'].join(':');
  const projectStorageKey = (config) => `hausSafety:v2:selectedProject:${scopeKey(config)}`;
  const ackStorageKey = (config, briefId, revision) => `hausSafety:v2:ack:${scopeKey(config)}:${briefId}:r${revision}`;

  const queryAll = (root, selector) => Array.from(root.querySelectorAll(selector));
  const setText = (root, selector, value) => queryAll(root, selector).forEach((node) => { node.textContent = value == null ? '' : String(value); });
  const setInputs = (root, selector, value) => queryAll(root, selector).forEach((node) => { node.value = value == null ? '' : String(value); });

  const parsePayload = (root) => {
    const node = root.querySelector('[data-haus-safety-config]');
    if (!node) return null;
    const payload = node ? safeParse(node.textContent, null) : null;
    return payload && typeof payload === 'object' ? payload : null;
  };

  const normalizeProject = (project, fallbackKey = 'default') => {
    const source = project || {};
    const id = source.id || source.projectId || source.key || slug(source.name || fallbackKey);
    return {
      ...source,
      id: String(id),
      name: source.name || 'Active Project',
      location: source.location || 'Company location',
      risk: source.risk || 'General',
      weather: source.weather || 'Conditions awaiting project update',
      weatherUpdated: source.weatherUpdated || 'configured project data'
    };
  };

  const chooseTopic = (instance, risk, dateKey) => {
    const { config } = instance;
    const project = instance.project;
    if (config.dailyEngineEnabled === false) {
      return {
        id: 'MANUAL',
        category: project.risk || risk || 'General',
        title: project.focusTitle || 'Project safety focus',
        text: project.focusText || 'Review the current work and site conditions before beginning.',
        action: project.focusAction || 'Confirm the plan and required controls with the crew.'
      };
    }

    if (/critical|high/i.test(String(project.risk || '')) && project.focusTitle) {
      return {
        id: 'OVERRIDE', category: project.risk, title: project.focusTitle,
        text: project.focusText, action: project.focusAction
      };
    }

    const custom = Array.isArray(instance.payload.dailyTopics)
      ? instance.payload.dailyTopics.filter((topic) => topic && topic.title)
      : [];
    const allTopics = [...TOPICS, ...custom];
    const role = String(config.role || '').toLowerCase();
    const work = String(config.workType || '').toLowerCase();
    const personaMatch = (topic) => {
      const roleTags = String(topic.roleTags || '').split(',').map((tag) => tag.trim()).filter(Boolean);
      const workTags = String(topic.workTags || '').split(',').map((tag) => tag.trim()).filter(Boolean);
      const roleMatch = !roleTags.length || roleTags.some((tag) => role.includes(tag) || tag.includes(role));
      const workMatch = !workTags.length || workTags.some((tag) => work.includes(tag) || tag.includes(work));
      return roleMatch && workMatch;
    };
    const customTargeted = custom.filter((topic) => (topic.roleTags || topic.workTags) && personaMatch(topic));
    const personaTopics = allTopics.filter(personaMatch);
    const base = customTargeted.length ? customTargeted : (personaTopics.length ? personaTopics : allTopics);
    const condition = riskKey(risk);
    const weatherTopics = condition === 'default' ? [] : base.filter((topic) => riskKey(topic.category) === condition);
    const generalTopics = base.filter((topic) => riskKey(topic.category) === 'default');
    const pool = weatherTopics.length ? weatherTopics : (generalTopics.length ? generalTopics : base);
    const seed = `${dateKey}|${project.id}|${config.role}|${config.workType}|${condition}`;
    return pool[hashString(seed) % pool.length] || TOPICS[0];
  };

  const parseWindMph = (windSpeed) => {
    const values = String(windSpeed || '').match(/\d+(?:\.\d+)?/g);
    return values && values.length ? Math.max(...values.map(Number)) : 0;
  };

  const classifyWeather = (instance, weather) => {
    const config = instance.config;
    const alertText = (weather.alerts || []).map((alert) => `${alert.event || ''} ${alert.headline || ''}`).join(' ');
    const combined = `${alertText} ${weather.shortForecast || ''}`.toLowerCase();
    if (/tornado|severe thunderstorm|lightning/.test(combined)) return 'Lightning';
    if (/excessive heat|heat advisory|extreme heat/.test(combined) || Number(weather.temperature) >= Number(config.heatThreshold || 90)) return 'Heat';
    if (/high wind|wind advisory|strong wind|damaging wind/.test(combined) || Number(weather.windMph) >= Number(config.windThreshold || 25)) return 'Wind';
    if (/winter|ice|freez|snow|cold|blizzard/.test(combined) || Number(weather.temperature) <= Number(config.coldThreshold || 32)) return 'Cold';
    if (/flood|rain|shower|drizzle|wet/.test(combined)) return 'Rain';
    return instance.project.risk || 'General';
  };

  const fetchJson = async (url, options = {}, timeout = 8000) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      if (!response.ok) {
        const error = new Error(`Request returned ${response.status}`);
        error.status = response.status;
        throw error;
      }
      if (response.status === 204) return {};
      return await response.json();
    } finally {
      window.clearTimeout(timer);
    }
  };

  const loadNwsWeather = async (instance) => {
    const project = instance.project;
    const latitude = Number.parseFloat(project.latitude);
    const longitude = Number.parseFloat(project.longitude);
    const configured = {
      summary: project.weather || 'Conditions awaiting project update',
      source: project.weatherUpdated ? `Configured project data Â· ${project.weatherUpdated}` : 'Configured project data',
      risk: project.risk || 'General',
      alerts: [],
      freshness: 'configured'
    };
    if (instance.config.liveWeatherEnabled === false || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return configured;
    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) return configured;

    const cacheKey = `hausSafety:v2:weather:${latitude.toFixed(4)},${longitude.toFixed(4)}`;
    const cached = readStorage(cacheKey);
    if (cached && Date.now() - Number(cached.savedAt || 0) < WEATHER_TTL && cached.value) return cached.value;

    try {
      const headers = { Accept: 'application/geo+json' };
      const [pointData, alertData] = await Promise.all([
        fetchJson(`https://api.weather.gov/points/${latitude},${longitude}`, { headers }),
        fetchJson(`https://api.weather.gov/alerts/active?point=${latitude},${longitude}`, { headers })
      ]);
      const hourlyUrl = pointData && pointData.properties && pointData.properties.forecastHourly;
      if (!hourlyUrl || !String(hourlyUrl).startsWith('https://api.weather.gov/')) throw new Error('Hourly forecast endpoint unavailable');
      const hourlyData = await fetchJson(hourlyUrl, { headers });
      const period = hourlyData && hourlyData.properties && hourlyData.properties.periods && hourlyData.properties.periods[0];
      if (!period) throw new Error('Hourly forecast unavailable');

      const severityRank = { Extreme: 4, Severe: 3, Moderate: 2, Minor: 1, Unknown: 0 };
      const alerts = Array.isArray(alertData && alertData.features)
        ? alertData.features.map((feature) => feature.properties || {}).sort((a, b) => (severityRank[b.severity] || 0) - (severityRank[a.severity] || 0)).slice(0, 5)
        : [];
      const topAlert = alerts[0] || null;
      const checked = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      const condition = `${period.temperature}Â°${period.temperatureUnit || 'F'} Â· ${period.shortForecast || 'Current forecast'} Â· Wind ${period.windDirection || ''} ${period.windSpeed || ''}`.trim();
      const weather = {
        summary: topAlert ? `${topAlert.headline || topAlert.event} â€” ${condition}` : condition,
        source: `National Weather Service Â· checked ${checked}`,
        provider: 'National Weather Service',
        retrievedAt: new Date().toISOString(),
        freshness: 'current',
        temperature: period.temperature,
        temperatureUnit: period.temperatureUnit || 'F',
        windSpeed: period.windSpeed || '',
        windMph: parseWindMph(period.windSpeed),
        shortForecast: period.shortForecast || '',
        alerts: alerts.map((alert) => ({
          id: alert.id || alert['@id'] || alert.headline || alert.event,
          event: alert.event || '', headline: alert.headline || alert.event || '',
          severity: alert.severity || 'Unknown', urgency: alert.urgency || 'Unknown',
          effective: alert.effective || null, expires: alert.expires || null
        }))
      };
      weather.risk = classifyWeather(instance, weather);
      writeStorage(cacheKey, { savedAt: Date.now(), value: weather });
      return weather;
    } catch (error) {
      return { ...configured, source: 'Configured project data Â· live NWS update unavailable', freshness: 'fallback', error: true };
    }
  };

  const fallbackRecord = (instance, weather) => {
    const date = getDateParts(instance.config.timeZone);
    const condition = riskKey(weather.risk || instance.project.risk);
    const projectKey = slug(instance.project.id || instance.project.name);
    const briefHash = hashString(`${date.key}|${projectKey}`).toString(36).toUpperCase().slice(0, 5);
    const briefId = `SSC-FB-${date.key}-${projectKey.toUpperCase().slice(0, 24)}-${briefHash}`;
    const storeKey = `hausSafety:v2:fallbackBrief:${scopeKey(instance.config)}:${briefId}`;
    const previous = readStorage(storeKey);

    let topic;
    if (previous && previous.topic && previous.conditionKey === condition) {
      topic = previous.topic;
    } else {
      topic = chooseTopic(instance, weather.risk || instance.project.risk, date.key);
    }
    const precautions = PRECAUTIONS[condition] || PRECAUTIONS.default;
    const materialSignature = `${topic.id}|${condition}|${topic.action}`;
    const revision = previous && previous.materialSignature && previous.materialSignature !== materialSignature
      ? Number(previous.revision || 1) + 1
      : Number(previous && previous.revision || 1);

    const stored = { topic, conditionKey: condition, materialSignature, revision };
    writeStorage(storeKey, stored);
    const localAcknowledgment = readStorage(ackStorageKey(instance.config, briefId, revision));
    return {
      contractVersion: '1.0-fallback',
      mode: instance.config.preview ? 'preview' : 'fallback',
      brief: {
        briefId,
        revision,
        dateDisplay: date.display,
        title: topic.title,
        summary: topic.text,
        requiredAction: topic.action,
        category: topic.category || weather.risk || 'General',
        riskLevel: String(weather.risk || instance.project.risk || 'General'),
        riskLabel: `${topic.category || weather.risk || 'General'} focus`,
        precautions
      },
      context: {
        projectId: instance.project.id,
        projectName: instance.project.name,
        projectLocation: instance.project.location
      },
      weather,
      acknowledgment: {
        status: localAcknowledgment ? 'acknowledged_local' : 'fallback_unrecorded',
        acknowledgedAt: localAcknowledgment && localAcknowledgment.acknowledgedAt || null
      }
    };
  };

  const normalizeLiveRecord = (data, instance) => {
    if (!data || !data.brief || !data.brief.brief_id) throw new Error('Safety service response did not match the v1 contract');
    const brief = data.brief;
    const risk = brief.risk || {};
    const effectiveDate = brief.effective_at ? new Date(brief.effective_at) : new Date();
    return {
      contractVersion: data.contract_version || '1.0',
      mode: 'live',
      brief: {
        briefId: brief.brief_id,
        revision: Number(brief.revision || 1),
        dateDisplay: Number.isNaN(effectiveDate.getTime()) ? getDateParts(instance.config.timeZone).display : effectiveDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }),
        title: brief.title,
        summary: brief.summary,
        requiredAction: brief.required_action,
        category: risk.label || risk.level || 'General',
        riskLevel: risk.level || 'general',
        riskLabel: risk.label || `${risk.level || 'General'} focus`,
        precautions: Array.isArray(brief.precautions) ? brief.precautions : PRECAUTIONS.default
      },
      context: {
        projectId: data.context && data.context.project_id || instance.project.id,
        projectName: data.context && data.context.project_name || instance.project.name,
        projectLocation: data.context && data.context.location_name || instance.project.location
      },
      weather: {
        summary: data.weather && data.weather.summary || instance.project.weather,
        source: data.weather && `${data.weather.provider || 'HAUS Safety service'}${data.weather.retrieved_at ? ` Â· ${data.weather.retrieved_at}` : ''}`,
        provider: data.weather && data.weather.provider || 'HAUS Safety service',
        freshness: data.weather && data.weather.freshness || 'current',
        alerts: data.weather && Array.isArray(data.weather.active_alerts) ? data.weather.active_alerts : [],
        risk: risk.label || risk.level || 'General'
      },
      acknowledgment: {
        status: data.acknowledgment && data.acknowledgment.status || 'new',
        acknowledgedAt: data.acknowledgment && data.acknowledgment.acknowledged_at || null
      }
    };
  };

  const statusLabel = (record) => {
    const status = record.acknowledgment && record.acknowledgment.status;
    if (status === 'acknowledged') return 'Acknowledged âœ“';
    if (status === 'reviewed') return 'Reviewed Â· acknowledgment pending';
    if (status === 'updated_review_required') return 'Updated â€” review required';
    if (status === 'acknowledged_local') return 'Acknowledged locally âœ“';
    if (record.mode === 'preview') return 'Theme Editor preview';
    if (record.mode === 'fallback') return 'Configured fallback Â· not recorded centrally';
    return 'Ready for acknowledgement';
  };

  const renderRecord = (instance, record) => {
    const root = instance.root;
    const brief = record.brief;
    const weather = record.weather || {};
    const context = record.context || {};
    instance.record = record;
    root.dataset.hausSafetyMode = record.mode;
    root.dataset.hausSafetyBriefId = brief.briefId;
    root.dataset.hausSafetyRevision = String(brief.revision);

    setText(root, '[data-daily-date], [data-brief-date], [data-he-brief-date]', brief.dateDisplay);
    setText(root, '[data-brief-id], [data-he-brief-id]', `${brief.briefId} Â· R${brief.revision}`);
    setText(root, '[data-brief-version]', `${brief.briefId} Â· Revision ${brief.revision}`);
    setText(root, '[data-focus-title], [data-he-focus-title]', brief.title);
    setText(root, '[data-focus-text], [data-he-focus-text]', brief.summary);
    setText(root, '[data-focus-action], [data-he-focus-action]', brief.requiredAction);
    setText(root, '[data-project-heading], [data-he-project]', context.projectName || instance.project.name);
    setText(root, '[data-project-location]', context.projectLocation || instance.project.location);
    setText(root, '[data-persona], [data-he-persona]', `${instance.config.role || 'Field Team'} Â· ${instance.config.workType || 'General Construction'}`);
    setText(root, '[data-weather-summary], [data-he-weather-summary]', weather.summary || 'Conditions awaiting project update');
    setText(root, '[data-weather-source], [data-he-weather-source]', record.mode === 'live' ? (weather.provider || 'HAUS Safety service') : (weather.source || 'Configured fallback'));
    setText(root, '[data-he-weather-metrics]', weather.source || weather.freshness || 'Current project context');
    setText(root, '[data-he-weather-hazard]', brief.riskLabel);
    setText(root, '[data-he-weather-advisory]', weather.alerts && weather.alerts.length
      ? 'Official weather alert active. Review the full briefing and follow the site emergency and shutdown plan.'
      : 'Weather screening supports planning; follow the employerâ€™s site-specific program and competent-person direction.');
    setText(root, '[data-brief-status], [data-he-ack-status]', statusLabel(record));
    setText(root, '[data-ack-focus]', brief.title);
    setText(root, '[data-ack-version]', `${brief.dateDisplay} Â· ${brief.briefId} Â· Revision ${brief.revision}`);

    queryAll(root, '[data-risk-badge], [data-he-risk-badge]').forEach((badge) => {
      badge.textContent = brief.riskLabel;
      badge.dataset.risk = String(brief.riskLevel || brief.category || 'general').toLowerCase();
    });
    const hazard = riskKey([
      brief.riskLevel,
      brief.category,
      brief.title,
      brief.summary,
      weather.summary,
      ...(weather.alerts || []).map((alert) => alert.event || alert.headline || '')
    ].join(' '));
    queryAll(root, '[data-he-weather]').forEach((node) => { node.dataset.hazard = hazard; });

    queryAll(root, '[data-precautions], [data-he-precautions]').forEach((list) => {
      list.replaceChildren(...brief.precautions.map((item) => {
        const row = document.createElement('li');
        row.textContent = item;
        return row;
      }));
    });

    const ackButton = root.querySelector('[data-ack-button]');
    if (ackButton) {
      const acknowledged = ['acknowledged', 'acknowledged_local'].includes(record.acknowledgment.status);
      ackButton.textContent = acknowledged ? 'Acknowledged âœ“' : (record.acknowledgment.status === 'updated_review_required' ? 'Review updated briefing' : 'Acknowledge todayâ€™s briefing');
    }

    setInputs(root, '[data-form-project]', context.projectName || instance.project.name);
    setInputs(root, '[data-form-focus]', brief.title);
    setInputs(root, '[data-form-risk]', brief.riskLabel);
    setInputs(root, '[data-form-date]', brief.dateDisplay);
    setInputs(root, '[data-form-version]', `${brief.briefId} Â· Revision ${brief.revision}`);
    setInputs(root, '[data-form-role]', instance.config.role || '');
    setInputs(root, '[data-form-work-type]', instance.config.workType || '');
    setInputs(root, '[data-form-weather]', weather.summary || '');
    setInputs(root, '[data-form-weather-source]', weather.source || weather.provider || '');

    window.dispatchEvent(new CustomEvent('haus:safety:brief-updated', {
      detail: { scope: instance.scope, projectId: instance.project.id, record }
    }));
  };

  const renderLoading = (instance) => {
    setText(instance.root, '[data-brief-status], [data-he-ack-status]', 'Loading todayâ€™s safety intelligenceâ€¦');
    setText(instance.root, '[data-weather-source], [data-he-weather-source]', 'Connectingâ€¦');
  };

  const serviceRecord = async (instance) => {
    const config = instance.config;
    if (config.preview || config.liveServiceEnabled === false || !config.endpointBase) return null;
    if (instance.serviceUnavailableUntil > Date.now()) return null;
    const url = `${String(config.endpointBase).replace(/\/$/, '')}/today?project_id=${encodeURIComponent(instance.project.id)}`;
    try {
      const data = await fetchJson(url, { headers: { Accept: 'application/json' } }, 6000);
      instance.serviceUnavailableUntil = 0;
      return normalizeLiveRecord(data, instance);
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        instance.serviceDenied = true;
        throw error;
      }
      instance.serviceUnavailableUntil = Date.now() + SERVICE_RETRY_TTL;
      return null;
    }
  };

  const refresh = async (instance, options = {}) => {
    const requestId = ++instance.requestId;
    if (!instance.record || options.showLoading) renderLoading(instance);
    try {
      const live = await serviceRecord(instance);
      if (requestId !== instance.requestId) return;
      if (live) {
        renderRecord(instance, live);
        return;
      }
    } catch (error) {
      if (requestId !== instance.requestId) return;
      setText(instance.root, '[data-brief-status], [data-he-ack-status]', 'Safety service access review required');
      setText(instance.root, '[data-weather-source], [data-he-weather-source]', 'Authenticated service unavailable');
      return;
    }

    const weather = await loadNwsWeather(instance);
    if (requestId !== instance.requestId) return;
    renderRecord(instance, fallbackRecord(instance, weather));
  };

  const postProjectSelection = async (instance) => {
    if (instance.config.preview || instance.config.liveServiceEnabled === false || !instance.config.endpointBase) return;
    const url = `${String(instance.config.endpointBase).replace(/\/$/, '')}/project-selection`;
    try {
      await fetchJson(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: instance.project.id })
      }, 6000);
    } catch (error) {
      // The local selection remains available as an explicitly non-authoritative fallback.
    }
  };

  const selectProject = (instance, key, options = {}) => {
    const project = normalizeProject(instance.projects[key] || instance.payload.default, key);
    instance.projectKey = key || project.id;
    instance.project = project;
    if (options.persist !== false) {
      writeStorage(projectStorageKey(instance.config), { key: instance.projectKey, project, savedAt: Date.now() });
      window.dispatchEvent(new CustomEvent('haus:safety:project-changed', { detail: { scope: instance.scope, key: instance.projectKey, project } }));
      postProjectSelection(instance);
    }
    refresh(instance, { showLoading: options.showLoading !== false });
  };

  const formStatus = (form, message, isError = false) => {
    let node = form.querySelector('[data-haus-form-status]');
    if (!node) {
      node = document.createElement('div');
      node.dataset.hausFormStatus = '';
      node.setAttribute('role', 'status');
      form.prepend(node);
    }
    node.className = isError ? 'hef-safety__form-error' : 'hef-safety__success';
    node.textContent = message;
  };

  const bindAcknowledgment = (instance) => {
    const form = instance.root.querySelector('form[data-haus-safety-ack-form], [data-haus-safety-ack-form] form');
    if (!form || form.dataset.hausSafetyBound === 'true') return;
    form.dataset.hausSafetyBound = 'true';
    form.addEventListener('submit', async (event) => {
      const record = instance.record;
      if (!record) return;
      if (record.mode !== 'live') {
        writeStorage(ackStorageKey(instance.config, record.brief.briefId, record.brief.revision), { acknowledgedAt: new Date().toISOString() });
        record.acknowledgment = { status: 'acknowledged_local', acknowledgedAt: new Date().toISOString() };
        renderRecord(instance, record);
        return;
      }

      event.preventDefault();
      const submit = form.querySelector('[type="submit"]');
      if (submit) submit.disabled = true;
      const url = `${String(instance.config.endpointBase).replace(/\/$/, '')}/briefs/${encodeURIComponent(record.brief.briefId)}/acknowledgments`;
      try {
        const response = await fetchJson(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'Idempotency-Key': `${record.brief.briefId}:r${record.brief.revision}:${instance.config.customerId || 'customer'}`
          },
          body: JSON.stringify({
            brief_id: record.brief.briefId,
            revision: record.brief.revision,
            project_id: record.context.projectId,
            status: 'acknowledged'
          })
        }, 8000);
        const acknowledgment = response.acknowledgment || {};
        record.acknowledgment = {
          status: acknowledgment.status || 'acknowledged',
          acknowledgedAt: acknowledgment.acknowledged_at || acknowledgment.acknowledgedAt || new Date().toISOString()
        };
        renderRecord(instance, record);
        formStatus(form, 'Acknowledgment recorded by HAUS Safety Intelligence.');
        window.setTimeout(() => form.closest('dialog') && form.closest('dialog').close(), 800);
      } catch (error) {
        formStatus(form, 'Acknowledgment could not be recorded. Nothing was marked complete. Please retry or contact your safety lead.', true);
      } finally {
        if (submit) submit.disabled = false;
      }
    });
  };

  const bindInterface = (instance) => {
    const root = instance.root;
    const select = root.querySelector('[data-project-select]');
    if (select) {
      select.addEventListener('change', () => selectProject(instance, select.value));
    }
    queryAll(root, '[data-open-dialog]').forEach((button) => button.addEventListener('click', () => {
      const dialog = root.querySelector(`[data-dialog="${button.dataset.openDialog}"]`);
      if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
    }));
    queryAll(root, '[data-close-dialog]').forEach((button) => button.addEventListener('click', () => {
      const dialog = button.closest('dialog');
      if (dialog) dialog.close();
    }));
    queryAll(root, 'dialog').forEach((dialog) => dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    }));
    const scrollBrief = root.querySelector('[data-scroll-brief]');
    if (scrollBrief) scrollBrief.addEventListener('click', () => {
      const brief = root.querySelector('.hef-safety__brief');
      if (brief) brief.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    bindAcknowledgment(instance);
  };

  const initRoot = (root) => {
    if (!root || instances.has(root) || root.dataset.hausSafetyInitialized === 'true') return;
    const payload = parsePayload(root);
    if (!payload) return;
    const config = {
      dailyEngineEnabled: true,
      liveWeatherEnabled: true,
      liveServiceEnabled: true,
      endpointBase: DEFAULT_ENDPOINT,
      timeZone: 'America/Indiana/Indianapolis',
      role: 'Field Team',
      workType: 'General Construction',
      ...payload.config
    };
    const projects = payload.projects && typeof payload.projects === 'object' ? payload.projects : {};
    const defaultProject = normalizeProject(payload.default || payload.project, 'default');
    if (!Object.keys(projects).length) projects[defaultProject.id] = defaultProject;
    Object.keys(projects).forEach((key) => { projects[key] = normalizeProject(projects[key], key); });

    const instance = {
      root, payload, config, projects,
      scope: scopeKey(config),
      projectKey: defaultProject.id,
      project: defaultProject,
      record: null,
      requestId: 0,
      serviceUnavailableUntil: 0,
      serviceDenied: false,
      timer: null
    };
    instances.set(root, instance);
    root.dataset.hausSafetyInitialized = 'true';
    root.dataset.hausSafetyVersion = VERSION;
    bindInterface(instance);

    const saved = readStorage(projectStorageKey(config));
    const select = root.querySelector('[data-project-select]');
    let initialKey = defaultProject.id;
    if (saved && saved.key && projects[saved.key]) initialKey = saved.key;
    else if (select && select.value && projects[select.value]) initialKey = select.value;
    else if (saved && saved.project && !select) {
      const savedProject = normalizeProject(saved.project, saved.key || 'saved');
      projects[savedProject.id] = savedProject;
      initialKey = savedProject.id;
    } else if (projects[initialKey] == null) initialKey = Object.keys(projects)[0];
    if (select && Array.from(select.options).some((option) => option.value === initialKey)) select.value = initialKey;
    selectProject(instance, initialKey, { persist: false, showLoading: true });

    instance.timer = window.setInterval(() => refresh(instance), WEATHER_TTL);
  };

  const rootsWithin = (scope = document) => {
    const roots = queryAll(scope, ROOT_SELECTOR);
    if (scope !== document && scope.matches && scope.matches(ROOT_SELECTOR)) roots.unshift(scope);
    return roots;
  };
  const initAll = (scope = document) => rootsWithin(scope).forEach(initRoot);
  const destroyWithin = (scope) => rootsWithin(scope).forEach((root) => {
    const instance = instances.get(root);
    if (instance && instance.timer) window.clearInterval(instance.timer);
    instances.delete(root);
  });

  window.addEventListener('haus:safety:project-changed', (event) => {
    queryAll(document, ROOT_SELECTOR).forEach((root) => {
      const instance = instances.get(root);
      if (!instance || instance.scope !== event.detail.scope || instance.project.id === event.detail.project.id) return;
      instance.projects[event.detail.project.id] = normalizeProject(event.detail.project, event.detail.key);
      selectProject(instance, event.detail.project.id, { persist: false, showLoading: true });
    });
  });

  window.addEventListener('storage', (event) => {
    if (!event.key || !event.key.startsWith('hausSafety:v2:selectedProject:') || !event.newValue) return;
    const saved = safeParse(event.newValue, null);
    if (!saved || !saved.project) return;
    queryAll(document, ROOT_SELECTOR).forEach((root) => {
      const instance = instances.get(root);
      if (!instance || event.key !== projectStorageKey(instance.config)) return;
      const project = normalizeProject(saved.project, saved.key);
      instance.projects[project.id] = project;
      selectProject(instance, project.id, { persist: false, showLoading: true });
    });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    queryAll(document, ROOT_SELECTOR).forEach((root) => {
      const instance = instances.get(root);
      if (instance) refresh(instance);
    });
  });
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));

  window.HausSafetyV2 = { version: VERSION, initAll, refreshAll: () => queryAll(document, ROOT_SELECTOR).forEach((root) => {
    const instance = instances.get(root);
    if (instance) refresh(instance, { showLoading: true });
  }) };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
})();